import { useEffect, useState } from "react";
import { useLocation } from "@tanstack/react-router";
import { Stamp, Menu, X } from "lucide-react";

const homeNav = [
  { hash: "#collections", path: "/#collections", label: "Collections" },
  { hash: "#about", path: "/#about", label: "About" },
  { hash: "/blog", path: "/blog", label: "Blog" },
  { hash: "#locations", path: "/#locations", label: "Visit" },
  { hash: "#want-list", path: "/#want-list", label: "Want List" },
  { hash: "#contact", path: "/#contact", label: "Contact" },
];

export function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const onHome = pathname === "/";
  const nav = homeNav.map((n) => ({
    href: n.hash.startsWith("/") ? n.path : onHome ? n.hash : n.path,
    label: n.label,
  }));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b transition-all duration-300 ${
        scrolled
          ? "border-border/60 bg-background/90 backdrop-blur-md"
          : "border-transparent bg-background/80 backdrop-blur-sm"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
        <a href={onHome ? "#top" : "/"} className="group flex items-center gap-2.5">
          <span className="grid h-9 w-9 place-items-center rounded-sm bg-primary text-primary-foreground shadow-[var(--shadow-stamp)] transition-transform group-hover:-rotate-3">
            <Stamp className="h-4.5 w-4.5" strokeWidth={2} aria-hidden="true" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-display text-base font-semibold tracking-tight">
              Carolina Mountain Stamps
            </span>
            <span className="mt-0.5 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              Hendersonville · Est. philately
            </span>
          </span>
        </a>

        <nav className="hidden items-center gap-6 lg:gap-8 md:flex" aria-label="Primary">
          {nav.map((n) => (
            <a
              key={n.href}
              href={n.href}
              className={`text-sm transition-colors hover:text-accent ${
                n.href === "/blog" && pathname.startsWith("/blog")
                  ? "text-accent"
                  : "text-foreground"
              }`}
            >
              {n.label}
            </a>
          ))}
          <a
            href="https://www.ebay.com/str/carolinamountainstamps"
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex min-h-11 items-center rounded-sm bg-accent px-4 py-2 text-sm font-medium text-accent-foreground shadow-[var(--shadow-stamp)] transition-transform hover:-translate-y-0.5"
          >
            Shop on eBay
            <span className="sr-only">(opens in a new tab)</span>
          </a>
        </nav>

        <button
          type="button"
          className="inline-flex min-h-12 min-w-12 items-center justify-center rounded-sm p-2 text-foreground md:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="mobile-nav"
          aria-label={open ? "Close menu" : "Open menu"}
        >
          {open ? (
            <X className="h-5 w-5" aria-hidden="true" />
          ) : (
            <Menu className="h-5 w-5" aria-hidden="true" />
          )}
        </button>
      </div>

      {open && (
        <div
          id="mobile-nav"
          className="border-t border-border/60 bg-background/95 px-6 py-4 backdrop-blur md:hidden"
        >
          <nav className="flex flex-col gap-1" aria-label="Mobile">
            {nav.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className="inline-flex min-h-12 items-center text-sm text-foreground"
              >
                {n.label}
              </a>
            ))}
            <a
              href="https://www.ebay.com/str/carolinamountainstamps"
              target="_blank"
              rel="noreferrer noopener"
              className="mt-2 inline-flex min-h-12 items-center justify-center rounded-sm bg-accent px-4 py-2 text-sm font-medium text-accent-foreground"
            >
              Shop on eBay
              <span className="sr-only">(opens in a new tab)</span>
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
